import { Injectable } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { MatDialogRef } from '@angular/material/dialog';
import { PanelErrorStatusService } from 'src/app/tech-support/services/panel-error-status-service';
import { WebappSubscriptionService } from 'src/app/shared/services/webapp-subscription.service';
import { PanelChange, WebppSocketService } from 'src/app/shared/services/webppSocket.service';
import { PanelErrorsComponent } from 'src/app/panel/components/panel-errors/panel-errors.component';
import { PanelSocketService } from 'src/app/shared/services/panelsocket.service';
import { EventService } from 'src/app/panel/services/event.service';
import { searchAvailableErrors } from 'src/app/panel/panel-utills';
import { Panel } from 'src/app/panel/models/panel.model';
import { Partition } from 'src/app/partitions/models/partition';
import { Device } from 'src/app/partitions/models/device';
import constants from '../constants';
import { LoadingBarService } from '@ngx-loading-bar/core';
import { PanelService } from 'src/app/panel/services/panel.service';
import { AuthService } from 'src/app/auth/services/auth.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslateService } from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';
import { CompanyUserService } from 'src/app/settings/services/company-user.service';

const LIST_LOAD_TIMEOUT_MS = 60000;
const MAX_LIST_LOAD_ATTEMPTS = 2;
/**
 * A partir de este corte se considera que el estado en pantalla quedó viejo y hay que volver a
 * pedir la lista. Muy por debajo de la hora que mira getStatus() para el color del marcador.
 */
const STALE_AFTER_OUTAGE_MS = 60000;

// La barra por defecto es compartida por toda la aplicación y cuenta referencias: un start() sin
// su complete() la deja girando para siempre. La carga de paneles usa su propia referencia y la
// libera con stop(), que fuerza el contador a cero en vez de restarle uno.
const PANEL_LIST_LOADING_BAR = 'panels-list';
const PANEL_LIST_REQUEST_KEY = 'panels-list';

@Injectable({
  providedIn: 'root',
})
export class PanelsService {
  panels: Panel[] = [];
  inactivePanels: Panel[] = [];
  panelsCount: number;
  accounts = [];
  dataSource: MatTableDataSource<Panel>;
  showActive = true;
  isLoadingResults = false;
  fetchingPanels = false;
  /** La carga agotó los reintentos: la UI ofrece reintentar o recargar en vez de girar sin fin. */
  listLoadFailed = false;

  reportSubscription: any;
  signalReportId = 99;
  signalReportIdC7 = 43;

  errorsDialogRef: MatDialogRef<PanelErrorsComponent>;
  availableBatteryStates = ['disconnected', 'low', 'failed'];

  snackBarDuration = 5000;
  snackBarErrorDuration = 10000;
  accountSelectState = 'fetching-accounts';

  removePanelFromMap;

  private listLoadTimeout: ReturnType<typeof setTimeout>;
  private listLoadInterval: ReturnType<typeof setInterval>;
  private listLoadAttempts = 0;
  private lastListLoadRequest: { getHistory: boolean; companyChosenByAdmin: string | null } = {
    getHistory: true,
    companyChosenByAdmin: null,
  };

  constructor(
    private readonly ps: PanelService,
    private readonly authService: AuthService,
    private readonly snackBar: MatSnackBar,
    private readonly translateService: TranslateService,
    public loadingBar: LoadingBarService,
    private readonly panelErrorStatusService: PanelErrorStatusService,
    private readonly webappSocket: WebappSubscriptionService,
    private readonly panelSocket: PanelSocketService,
    private readonly eventService: EventService,
    private readonly wsSocket: WebppSocketService,
    private readonly companyUserService: CompanyUserService
  ) {
    this.setup();
  }

  setup() {
    this.dataSource = new MatTableDataSource(this.panels);
    this.webappSocket.addSubscription(this.handleIncomingMessages, constants.webappSocketTypes.panelVitalsUpdate);
  }

  panelEnableOnFilter(panel, rs) {
    if (rs[0] === 'default') return true;

    return rs.includes(panel.apiKey);
  }

  public collectPanels = async (updateDataSource = false) => {
    if (this.panels?.length > 0) {
      this.panels.forEach((panel: Panel) => {
        if (panel.isRepeating || this.panelErrorStatusService.errorsPresent(panel)) {
          this.panelErrorStatusService.onNewError(panel._id);
        } else {
          this.panelErrorStatusService.onClean(panel._id);
        }
        panel.reloadCounter = 0;
        panel.errors = this.countErrors(panel);
      });

      if (updateDataSource) {
        this.dataSource.data = this.panels;
      }

      const auxAccounts = this.panels.map((panel: Panel) =>
        panel.partitions.map((partition: Partition) => ({
          account: partition.account,
          name: partition.name,
          mac: partition.mac,
        }))
      );
      this.accounts = [].concat(...auxAccounts);
      this.accounts.sort((a, b) => a.account.localeCompare(b.account));
      this.accountSelectState = this.accounts.length > 0 ? 'found-results' : 'no-results-found';
    }
  };

  // Método encargado de pasar al socket un array de apikeys de la empresa para activar la escucha, y luego se suscribe a los mensajes para filtrar
  // los que sean de reporte de señal, y de un equipo el cual esté en pantalla. Una vez cumplidas esas condiciones, envía un evento con la información
  // necesaria al componente signal-strength a través de un servicio.
  filterAndSendSignalReport = async (apiKeys: string[]) => {
    this.panelSocket.initMPListener(apiKeys);
    // Se reasignaba sin liberar la anterior, así que cada llamada dejaba otro suscriptor vivo
    // emitiendo el mismo reporte.
    this.reportSubscription?.unsubscribe();
    this.reportSubscription = this.panelSocket.listenEvent.subscribe((message) => {
      if (message.decodification) {
        if (message.decodification.id == this.signalReportId && message.decodification.signalType ||
          message.decodification.id == this.signalReportIdC7 && message.decodification.interface
        ) {
          const dec = message.decodification;
          let signal;
          let signalType;
          if (dec.id == this.signalReportId) {
            signal = dec.param;
            signalType = dec.signalType;
          } else {
            signal = Math.floor((dec.confirmed / dec.sent) * 100);
            if (signal > 100) signal = 100;
            signalType = dec.interface === '3G' ? 'mobile' : 'mesh' ;
          }
          this.dataSource.data.forEach((element) => {
            if (element.mac == message.device.mac) {
              this.eventService.signalUpdatedEvent.emit({
                signal,
                signalType,
                mac: message.device.mac,
              });
            }
          });
        }
      }
    });
  };

  handleIncomingMessages = (message: PanelChange) => {
    const panel = this.dataSource.data.find((p) => p.mac === message.mac);
    if (panel) {
      panel.reloadCounter += 1;

      if (this.errorsDialogRef && this.errorsDialogRef.componentInstance.data.id === panel._id) {
        this.errorsDialogRef.componentInstance.fetch();
      } else {
        this.panelErrorStatusService.onNewError(panel._id);
      }
    }
  };

  countErrors = (panel: Panel): number => {
    const panelErrorsCount = this.countVitalsErrors(panel);

    const devicesErrorsCount = panel.partitions.map((p: Partition) =>
      p.devices.map((d: Device) => this.errorsPresentDevice(d))
    );

    const initialValue = 0;
    const devicesErrorsAccumulators = devicesErrorsCount.map((arr) =>
      arr.reduce((accumulator, currentValue) => accumulator + currentValue, initialValue)
    );

    const devicesFinalErrorsCount = devicesErrorsAccumulators.reduce((acc, curr) => acc + curr, initialValue);

    return panelErrorsCount + devicesFinalErrorsCount;
  };

  countVitalsErrors = (element: Panel) => {
    let errors = 0;

    if (this.availableBatteryStates.includes((element.vitals?.batteryStatus || 'normal').toString())) errors++;
    if ((element.vitals?.energyStatus ?? '').toString() == 'false') errors++;
    if (element.vitals?.tampered) errors++;

    return errors;
  };

  errorsPresentDevice(element: Device) {
    return searchAvailableErrors(element).length;
  }

  clear = () => {
    this.panels = [];
    this.inactivePanels = [];
    this.panelsCount = undefined;
    this.accounts = [];
    this.dataSource.filter = undefined;
    this.showActive = true;
    this.dataSource.data = [];
    this.fetchingPanels = false;
    this.onPanelListLoaded();
  };

  getPanels = async (getHistory = true, companyChosenByAdmin = null) => {
    this.isLoadingResults = true;

    if (this.companyUserService.adminDidNotChooseCompany()) {
      this.panelsCount = 0;
      this.listLoadingBar.stop();
      return;
    }

    if (companyChosenByAdmin == null && this.companyUserService.adminHasChosenCompany()) {
      companyChosenByAdmin = this.companyUserService.getCompanyIdChosenByAdmin();
    }

    this.lastListLoadRequest = { getHistory, companyChosenByAdmin };

    this.wsSocket.requestDataToService(() => {
      if (this.fetchingPanels) return;

      this.fetchingPanels = true;
      this.listLoadFailed = false;
      this.startListLoadTimeout();
      // La barra sigue al pedido HTTP, no al streaming de paquetes por socket: los marcadores
      // siguen llegando después, pero la barra no debe quedar corriendo durante todo ese lote.
      this.listLoadingBar.start();
      this.listLoadInterval = setInterval(() => {
        if (this.panelSocket.receiversConnected) {
          this.clearListLoadInterval();
          this.ps.getPanels(getHistory, companyChosenByAdmin, this.wsSocket.socketId).subscribe({
            next: (res: any) => {
              this.panelsCount = res.count;
              this.listLoadingBar.stop();
            },
            error: (error) => {
              this.abandonListLoad();
              this.processError(error);
            },
          });
        }
      }, 500);
    }, PANEL_LIST_REQUEST_KEY);
  };

  /**
   * El backend entrega la lista de paneles en paquetes por socket y la cierra con un mensaje
   * 'finish'. Socket.IO no reenvía los paquetes emitidos mientras el cliente estuvo desconectado,
   * por lo que un corte a mitad de la carga deja la página cargando para siempre. Los métodos
   * siguientes detectan ese caso y reintentan la carga en lugar de quedarse trabados.
   */
  onPanelListLoaded() {
    this.clearListLoadInterval();
    this.clearListLoadTimeout();
    this.listLoadAttempts = 0;
    this.listLoadFailed = false;
  }

  /** Reintento pedido explícitamente por el usuario: reinicia el tope de intentos. */
  retryPanelListLoadFromUser() {
    this.listLoadAttempts = 0;
    this.listLoadFailed = false;
    this.wsSocket.cutAndReconnect();
    this.getPanels(this.lastListLoadRequest.getHistory, this.lastListLoadRequest.companyChosenByAdmin);
  }

  reloadPanelListOnReconnect() {
    const outageMs = this.wsSocket.consumeOutageMs();

    if (this.fetchingPanels) {
      this.retryListLoad();
      return;
    }

    // Una carga que llegó a terminar igual queda vieja tras un corte: el color de cada marcador
    // sale de los reportes de la última hora y, mientras el socket estuvo caído, no llegó ninguno.
    // Sin volver a pedir la lista el mapa se queda entero en rojo hasta que cada equipo reporte.
    // Lo mismo vale si la carga se dio por perdida: al volver la conexión hay que reintentar sola,
    // porque si no la única salida es que el usuario recargue la página.
    if (!this.listLoadFailed && outageMs < STALE_AFTER_OUTAGE_MS) return;

    this.listLoadAttempts = 0;
    this.listLoadFailed = false;
    this.getPanels(this.lastListLoadRequest.getHistory, this.lastListLoadRequest.companyChosenByAdmin);
  }

  private startListLoadTimeout() {
    this.clearListLoadTimeout();
    this.listLoadTimeout = setTimeout(() => this.onListLoadTimeout(), LIST_LOAD_TIMEOUT_MS);
  }

  private onListLoadTimeout() {
    if (!this.fetchingPanels) return;

    // El socket puede decir que está conectado y tener la tubería trabada. Cortarla y rehacerla es
    // más fiable que reintentar el pedido sobre la misma conexión.
    this.wsSocket.cutAndReconnect();
    this.retryListLoad();
  }

  private retryListLoad() {
    this.abandonListLoad();

    if (this.listLoadAttempts >= MAX_LIST_LOAD_ATTEMPTS) {
      this.listLoadAttempts = 0;
      this.listLoadFailed = true;
      return;
    }

    this.listLoadAttempts++;
    this.notifyListLoad('Panel.MapLoadRetrying', this.snackBarDuration);
    this.getPanels(this.lastListLoadRequest.getHistory, this.lastListLoadRequest.companyChosenByAdmin);
  }

  private abandonListLoad() {
    this.clearListLoadInterval();
    this.clearListLoadTimeout();
    this.fetchingPanels = false;
    this.isLoadingResults = false;
    this.listLoadingBar.stop();
  }

  private get listLoadingBar() {
    return this.loadingBar.useRef(PANEL_LIST_LOADING_BAR);
  }

  private clearListLoadTimeout() {
    if (this.listLoadTimeout === undefined) return;

    clearTimeout(this.listLoadTimeout);
    this.listLoadTimeout = undefined;
  }

  private clearListLoadInterval() {
    if (this.listLoadInterval === undefined) return;

    clearInterval(this.listLoadInterval);
    this.listLoadInterval = undefined;
  }

  private async notifyListLoad(translationKey: string, duration: number) {
    this.snackBar.open(
      await firstValueFrom(this.translateService.get(translationKey)),
      await firstValueFrom(this.translateService.get('Shared.Close')),
      { duration }
    );
  }

  processError = async (error) => {
    this.isLoadingResults = false;

    if (error && error.status == 401) {
      this.snackBar.open(
        await firstValueFrom(this.translateService.get('Shared.SessionExpired')),
        await firstValueFrom(this.translateService.get('Shared.Close')),
        { duration: this.snackBarErrorDuration }
      );
      this.authService.logout();
    } else {
      this.snackBar.open(
        await firstValueFrom(this.translateService.get('Panel.FailedGetDev')),
        await firstValueFrom(this.translateService.get('Shared.Close')),
        { duration: this.snackBarErrorDuration }
      );
    }
  };

  getPanel = async (mac) => {
    const panel = this.panels.find((p) => p.mac === mac);
    if (panel) {
      return panel;
    } else {
      this.ps.getPanel(undefined, mac).subscribe({
        next: (res: any) => {
          if (res.success) {
            return res.panel;
          }
        },
        error: (error) => {
          return undefined;
        },
      });
    }
  };

  removePanel = (mac) => {
    const panel = this.panels.find((p) => mac === p.mac);
    this.inactivePanels.push({ ...panel, active: false });

    this.panels = this.panels.filter((p) => mac !== p.mac);
    this.panelsCount = this.panelsCount - 1;

    this.dataSource.data = this.panels;

    if (this.removePanelFromMap) this.removePanelFromMap(panel);
  };

  toggleActive = () => {
    this.showActive = !this.showActive;
  };
}
